import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import usePut from '@/core/composables/api/usePut'
import { useApplicationModule } from '@/core/composables/use-application-module'
import { usePermission } from '@/core/composables/use-permission'
import type { UpdateMemberSettings } from '@/core/composables/use-member-settings'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { ApplicationModule } from '@/core/models/organization'
import { Permission, type EmailNotificationSettings } from '@/core/models/user'
import { useUserStore } from '@/stores/user.store'
import { useSnackStore } from '@/stores/snack.store'

export type NotificationGroupId = 'feedback' | 'dialog' | 'coaching' | 'assessment'

export type EmailNotificationItem = {
  key: string
  enabled: boolean
  label: string
  description: string
}

export type EmailNotificationGroup = {
  id: NotificationGroupId
  title: string
  items: EmailNotificationItem[]
}

const GROUP_ORDER: ReadonlyArray<NotificationGroupId> = [
  'feedback',
  'dialog',
  'coaching',
  'assessment',
]

type NotificationAccess = {
  can: (permission: Permission) => boolean
  canAny: (permissions: Permission[]) => boolean
  hasModule: (module: ApplicationModule) => boolean
}

const NOTIFICATION_TYPES: Record<
  string,
  { group: NotificationGroupId; isVisible: (access: NotificationAccess) => boolean }
> = {
  feedbackRequested: {
    group: 'feedback',
    isVisible: ({ can }) => can(Permission.FEEDBACK_GIVE),
  },
  feedbackTaskCreated: {
    group: 'feedback',
    isVisible: ({ canAny }) =>
      canAny([Permission.FEEDBACK_GIVE, Permission.FEEDBACK_RECEIVE, Permission.FEEDBACK_INITIATE]),
  },
  structuredFeedbackReceived: {
    group: 'feedback',
    isVisible: ({ can }) => can(Permission.FEEDBACK_RECEIVE),
  },
  unstructuredFeedbackReceived: {
    group: 'feedback',
    isVisible: ({ can }) => can(Permission.FEEDBACK_RECEIVE),
  },
  dialogDatePlanned: {
    group: 'dialog',
    isVisible: ({ can, hasModule }) =>
      hasModule(ApplicationModule.DIALOG) && can(Permission.FEEDBACK_RECEIVE),
  },
  dialogReleased: {
    group: 'dialog',
    isVisible: ({ canAny, hasModule }) =>
      hasModule(ApplicationModule.DIALOG) &&
      canAny([Permission.FEEDBACK_RECEIVE, Permission.COACHING_ASSIGN]),
  },
  dialogFinished: {
    group: 'dialog',
    isVisible: ({ can, hasModule }) =>
      hasModule(ApplicationModule.DIALOG) && can(Permission.FEEDBACK_RECEIVE),
  },
  coachingInvitationCreated: {
    group: 'coaching',
    isVisible: ({ hasModule }) => hasModule(ApplicationModule.COACHING),
  },
  assessmentPeerInvited: {
    group: 'assessment',
    isVisible: ({ hasModule }) => hasModule(ApplicationModule.ASSESSMENT),
  },
}

export const useNotificationSettings = () => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const userStore = useUserStore()
  const { user } = storeToRefs(userStore)
  const { can, canAny } = usePermission()
  const { hasModule } = useApplicationModule()
  const { mutate, status: updateStatus } = usePut<void, { settings: UpdateMemberSettings }>(
    'member/settings',
  )

  const emailSettings = ref<EmailNotificationSettings>({})
  let updateQueue: Promise<void> = Promise.resolve()

  watch(
    () => user.value.settings.notifications.email,
    (email) => {
      emailSettings.value = { ...email }
    },
    { immediate: true, deep: true },
  )

  const isVisibleNotification = (key: string) => {
    const notificationType = NOTIFICATION_TYPES[key]
    return notificationType?.isVisible({ can, canAny, hasModule }) ?? false
  }

  const visibleEmailSettings = computed(() =>
    Object.fromEntries(
      Object.entries(emailSettings.value).filter(([key]) => isVisibleNotification(key)),
    ),
  )

  const notificationGroups = computed<EmailNotificationGroup[]>(() => {
    const groups = new Map<NotificationGroupId, EmailNotificationItem[]>()

    for (const [key, enabled] of Object.entries(visibleEmailSettings.value)) {
      const notificationType = NOTIFICATION_TYPES[key]
      if (!notificationType) continue

      const items = groups.get(notificationType.group) ?? []
      items.push({
        key,
        enabled,
        label: t(`settings.notifications.email.${key}.title`),
        description: t(`settings.notifications.email.${key}.description`),
      })
      groups.set(notificationType.group, items)
    }

    return GROUP_ORDER.flatMap((groupId) => {
      const items = groups.get(groupId)
      if (!items?.length) return []

      return [
        {
          id: groupId,
          title: t(`settings.notifications.groups.${groupId}`),
          items,
        },
      ]
    })
  })

  const hasNotifications = computed(() => Object.keys(visibleEmailSettings.value).length > 0)

  const allEnabled = computed(
    () => hasNotifications.value && Object.values(visibleEmailSettings.value).every(Boolean),
  )

  const allDisabled = computed(
    () =>
      hasNotifications.value && Object.values(visibleEmailSettings.value).every((value) => !value),
  )

  const syncUserStoreEmail = (email: EmailNotificationSettings) => {
    const currentUser = userStore.user
    userStore.$patch({
      _user: {
        ...currentUser,
        settings: {
          ...currentUser.settings,
          notifications: {
            ...currentUser.settings.notifications,
            email: { ...email },
          },
        },
      },
    })
  }

  const persistEmailSettings = (email: EmailNotificationSettings) => {
    const previousValues = Object.fromEntries(
      Object.keys(email).map((key) => [key, emailSettings.value[key]]),
    ) as EmailNotificationSettings

    emailSettings.value = { ...emailSettings.value, ...email }

    updateQueue = updateQueue.then(async () => {
      await mutate({
        settings: { notifications: { email } },
      })

      if (updateStatus.value === ApiRequestStatus.Success) {
        syncUserStoreEmail(emailSettings.value)
        snackStore.showInfo(t('settings.notifications.snackbar.success'))
        return
      }

      emailSettings.value = {
        ...emailSettings.value,
        ...Object.fromEntries(
          Object.entries(previousValues).filter(([key]) => emailSettings.value[key] === email[key]),
        ),
      }
      snackStore.showError(t('settings.notifications.snackbar.error'))
    })

    return updateQueue
  }

  const setEmailNotification = (key: string, enabled: boolean) => {
    if (emailSettings.value[key] === enabled) return
    return persistEmailSettings({ [key]: enabled })
  }

  const setAllEmailNotifications = (enabled: boolean) => {
    const email = Object.fromEntries(
      Object.keys(visibleEmailSettings.value).map((key) => [key, enabled]),
    )
    if (Object.keys(email).length === 0) return
    return persistEmailSettings(email)
  }

  return {
    notificationGroups,
    hasNotifications,
    allEnabled,
    allDisabled,
    setEmailNotification,
    setAllEmailNotifications,
  }
}
